import { devLog, alwaysLog } from '@/utils/logger';
import { ref, watchEffect } from 'vue';
import { defineStore } from 'pinia';

const LONG_FORMAT_SESSION_KEY = 't4.isLongFormat';
const ACTIVE_PANEL_SESSION_KEY = 't4.activePanel';

function readSessionFlag(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

const PANEL_KEYS = new Set(['dashboard', 'text', 'voice', 'record', 'cache']);

function readSessionString(key: string, fallback: string): string {
  try {
    return sessionStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}

function readActivePanel(): string {
  const raw = readSessionString(ACTIVE_PANEL_SESSION_KEY, 'text');
  return PANEL_KEYS.has(raw) ? raw : 'text';
}

export const usePageState = defineStore('pageState', () => {
  const pageTitle = ref('视频编辑（T4-edit）');
  // 暗色模式
  const isDark = ref(Boolean(localStorage.theme) || true);
  devLog.log('🚀 ~ usePageState ~ isDark:', isDark);
  const isLoading = ref(localStorage.loadingPage === '1');
  const hideSubMenu = ref(localStorage.showSubmenu === '0');
  const hasUnsavedChanges = ref(false); // 新增：是否有未保存的更改
  const activePanel = ref(readActivePanel());
  /** 当前工程是否以 Long-format（流式）模式加载；影响视频轨高度与缩略图生成 */
  const isLongFormat = ref(readSessionFlag(LONG_FORMAT_SESSION_KEY));
  
  // 项目名称管理
  const projectName = ref(localStorage.getItem('t4.projectName') || '');

  // Drive 路径上下文
  const selectedDriveId = ref<string>(localStorage.getItem('t4.selectedDriveId') || '');
  const selectedFirstLevelFolderId = ref<string>(localStorage.getItem('t4.selectedFirstLevelFolderId') || '');
  const selectedSecondLevelFolderId = ref<string>(localStorage.getItem('t4.selectedSecondLevelFolderId') || '');

  watchEffect(() => {
    devLog.log(`switch to ${isDark.value ? 'dark' : 'light'}`);
    localStorage.theme = isDark.value ? 'true' : 'false';
    localStorage.loadingPage = isLoading.value ? '1' : '0';
    localStorage.hideSubMenu = hideSubMenu.value ? '1' : '0';
    localStorage.setItem('t4.projectName', projectName.value);
    document.documentElement.classList[isDark.value ? 'add' : 'remove']('dark');
  });

  // Auto Compose / 恢复流程依赖：long/short 与当前面板写入 sessionStorage
  watchEffect(() => {
    try {
      sessionStorage.setItem(LONG_FORMAT_SESSION_KEY, isLongFormat.value ? '1' : '0');
      sessionStorage.setItem(ACTIVE_PANEL_SESSION_KEY, activePanel.value);
    } catch {
      // sessionStorage 不可用时忽略
    }
  });

  // 属性宽度
  const attrWidth = ref(parseInt(localStorage.attrW || '320'));
  // 轨道高度
  const trackHeight = ref(parseInt(localStorage.trackH || '380'));
  watchEffect(() => {
    localStorage.attrW = attrWidth.value;
    localStorage.trackH = trackHeight.value;
  });

  // 添加beforeunload事件监听
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', event => {
      if (hasUnsavedChanges.value) {
        event.preventDefault();
        event.returnValue = '您的更改可能不会被保存，确定要离开吗？';
        return event.returnValue;
      }
      return undefined;
    });
  }

  // 设置未保存更改状态
  function setUnsavedChanges(value: boolean) {
    hasUnsavedChanges.value = value;
  }

  // 设置项目名称
  function setProjectName(name: string) {
    projectName.value = name;
  }

  return {
    hideSubMenu,
    isLoading,
    pageTitle,
    isDark,
    attrWidth,
    trackHeight,
    hasUnsavedChanges,
    setUnsavedChanges,
    activePanel,
    selectedDriveId,
    selectedFirstLevelFolderId,
    selectedSecondLevelFolderId,
    projectName,
    setProjectName,
    isLongFormat
  };
});
